import React, { useEffect } from 'react';

import { Zindexes } from '@zapier/theme';
import { Global, css } from '@emotion/core';

type GoogleOneTapCredential = {
  idToken: string;
};

type GoogleOneTapError = {
  type?: string;
  message?: string;
  reason?: string;
};

type Props = {
  /**
   * Optional CSS styles provided from emotion
   */
  className?: string;

  /**
   * Fired when Google One tap encounters an error.
   */
  onError: (error: GoogleOneTapError) => void;

  /**
   * Fired when Google One tap successfully logs in.
   */
  onSuccess: (credential: GoogleOneTapCredential) => void;

  /**
   * Enables the auto-login sequence if available. User must have previously
   * logged in with Google One Tap for this to work.
   */
  shouldAutologin: boolean;

  /** Optional callback fired when Google One Tap is loaded */
  onGoogleOneTapLoaded?: () => void;

  /** Google Client ID key, typically provided as an environment variable */
  googleClientId?: string;

  /**
   * Changes the words of the title and messages in the One Tap prompt to suit
   * the context it is used in.
   */
  displayContext: 'signin' | 'signup' | 'use';

  /**
   * Width of the One Tap iframe, defaults to `auto`.
   */
  width: string | number;
};

// This parent ID is used by the Javascript Google One Tap API, it allows us
// to mount One Tap in the wrapper rendered in the component's render method.
// This allows us to style the parent as we see fit.
const GOOGLE_ONE_TAP_PARENT_ID = 'google-one-tap-container';
const GOOGLE_ONE_TAP_TIMEOUT_SUPRESSION = 86400; // 24 hours measured in seconds - 24hrs is default
const GOOGLE_ONE_TAP_LOADING_TIMEOUT = 3000;

interface GoogleOneTapMessages {
  browser_not_supported: string;
  opt_out_or_no_session: string;
  user_cancel: string;
  suppressed_by_user: string;
  issuing_failed: string;
  invalid_client: string;
}

const humanReadableMessage: GoogleOneTapMessages = {
  browser_not_supported:
    'The current browser is not supported by Google One Tap.',
  opt_out_or_no_session:
    'There are no sessions available to be used by Google One Tap. Possible causes are that you are not logged in to Google or that an ad-blocker is preventing you from seeing your Google session.',
  user_cancel:
    'The user opt-ed out of the Google One Tap dialog by clicking the cancel button.',
  suppressed_by_user: `The user previously opt-ed out of Google One Tap. The dialog will be suppressed until the suppression timeout period ellapses (${GOOGLE_ONE_TAP_TIMEOUT_SUPRESSION} seconds) or cookies are cleared.`,
  issuing_failed:
    'Google failed to issue a credential. The selected session may have signed out of Google.',
  invalid_client:
    'An invalid `client_id` was provided. Check your Google Client ID and ensure it is configured correctly.',
};

const getHumanReadableMessage = (reason: keyof GoogleOneTapMessages) => {
  const message: string =
    humanReadableMessage[reason] ||
    `No human-readable message available for: ${reason}. See official docs for additional info. https://developers.google.com/identity/one-tap/web/reference/js-reference?authuser=1#google.accounts.id.prompt`;
  return `[Google One Tap]: ${message}`;
};

type GoogleOneTapAPIResponse = {
  clientId: string;
  credential: string;
  select_by: string;
};

// Fixes TS error about `window` and `document` not existing on `global` API
const globalAny: any = global;

const defaultProps = {
  displayContext: 'use',
  width: 'auto',
};

const GoogleOneTapV2 = (props: Props) => {
  const { onGoogleOneTapLoaded, displayContext, width } = {
    ...defaultProps,
    ...props,
  };

  useEffect(() => {
    // `hasLoaded` uses a local variable here instead of state as `setState`
    // would cause a re-render and that would trigger `useEffect` to be fired
    // again.
    let hasLoaded = false;

    // Add the Google One Tap script to the current context
    const oneTapScript = globalAny.document!.createElement('script');
    oneTapScript.setAttribute('type', 'text/javascript');
    oneTapScript.setAttribute('src', 'https://accounts.google.com/gsi/client');
    globalAny.document!.body.appendChild(oneTapScript);

    // Once the script has loaded we can begin the configuration process
    // for Google One Tap. If we try to do this immediately, we will encounter
    // a race condition where the script loads after we have attempted to
    // initialize One Tap.
    oneTapScript.onload = () => {
      const googleOneTapOptions = {
        auto_select: props.shouldAutologin,
        callback: (result: GoogleOneTapAPIResponse) => {
          props.onSuccess({ idToken: result.credential });
        },
        cancel_on_tap_outside: false,
        client_id: props.googleClientId,
        prompt_suppress_sec: GOOGLE_ONE_TAP_TIMEOUT_SUPRESSION,
        context: displayContext,
        state_cookie_domain: 'zapier.com',
        prompt_parent_id: GOOGLE_ONE_TAP_PARENT_ID,
      };

      // Initialize Google One Tap with the provided configuration
      globalAny.window!.google!.accounts!.id!.initialize(googleOneTapOptions);

      // This callback is fired when something occurs with Google One Tap.
      // This can be positive, or negative, so we must inspect the notification
      // in order to determine if a course of action must be taken (e.g. hide
      // Google One Tap)
      // For more information on the API and the error messages here, see the
      // official docs here: https://developers.google.com/identity/one-tap/web/reference/js-reference?authuser=1#google.accounts.id.prompt
      const onNotification = (notification: any) => {
        // `onNotification` will be called whenever Google One Tap successfully
        // initializes. So we mark `hasLoaded` as `true` so that we do not fall
        // into a timed out state.
        if (!hasLoaded) {
          hasLoaded = true;
        }

        const momentType = notification.getMomentType();

        // Display moment: This occurs after the prompt() method is called. The
        // notification contains a boolean value to indicate whether the UI is
        // displayed or not.
        if (momentType === 'display') {
          if (!notification.isDisplayed()) {
            const reason = notification.getNotDisplayedReason();

            props.onError({
              type: momentType,
              reason,
              message: getHumanReadableMessage(reason),
            });
          }

          // Call the `onGoogleOneTapLoaded` callback if one is provided and
          // Google One Tap has successfully loaded
          if (notification.isDisplayed() && onGoogleOneTapLoaded) {
            onGoogleOneTapLoaded();
          }
        }

        // Skipped moment: This occurs when the One Tap prompt is closed by an
        // auto cancel, a manual cancel, or when Google fails to issue a
        // credential, such as if the selected session has signed out of Google.
        // In these cases, it's recommended that you continue on to the next
        // identity providers, if there are any.
        if (momentType === 'skipped') {
          const reason = notification.getSkippedReason();

          props.onError({
            type: momentType,
            reason,
            message: getHumanReadableMessage(reason),
          });
        }

        // Dismissed moment: This occurs when a credential is successfully
        // retrieved by Google or a user wants to stop the credential retrieval
        // flow. For example, on your login dialog, when the user begins to
        // input their username and password, you can call the
        // google.accounts.id.cancel() method to close the One Tap prompt and
        // trigger a dismissed moment.
        if (momentType === 'dismissed') {
          const reason = notification.getDismissedReason();

          // `credential_returned` is returned when Google One Tap has
          // successfully returned a value. In this case, the
          // `googleOneTapOptions.callback` function will be fired.
          if (reason !== 'credential_returned') {
            props.onError({
              type: momentType,
              reason,
              message: getHumanReadableMessage(reason),
            });
          }
        }
      };

      // This will attempt to render the Google One Tap dialog. `onNotification`
      // will receive notifications that indicate the status of Google One Tap.
      globalAny.window!.google!.accounts!.id!.prompt(onNotification);
    };

    // The latest version of Google One Tap is prone to being unable to load
    // for a very long duration. This effectively blocks the UI from loading
    // alternative log-in options such as Google SSO. To avoid this we
    // check to see if One Tap has loaded after a specific duration and if
    // not we fire an error off.
    setTimeout(() => {
      if (!hasLoaded) {
        // Fire the cancel function to clear Google One Tap from the DOM just
        // in case it did happen to load async while we are going ahead with the
        // timeout flow.
        if (globalAny.window!.google) {
          globalAny.window.google.accounts!.id!.cancel();
        }

        props.onError({
          type: 'display',
          reason: 'loading_timeout',
          message: `Google One Tap was unable to load within the timeout duration, it exceeded the limit of ${GOOGLE_ONE_TAP_LOADING_TIMEOUT}ms.`,
        });
      }
    }, GOOGLE_ONE_TAP_LOADING_TIMEOUT);

    return () => {
      // Clean up the remains of Google One Tap when the component is unmounted
      if (globalAny.window!.google) {
        globalAny.window.google.accounts!.id!.cancel();
      }
    };

    // We only want `useEffect` to run on mount, otherwise we might end up
    // attempting to render one tap twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={props.className} id={GOOGLE_ONE_TAP_PARENT_ID}>
      {/*
        The styles below need to live in a `<Global>`. On desktop, one tap is
        mounted as a child of this `<div>`, however on mobile one tap will be
        mounted to the `<body>` tag.
      */}
      <Global
        styles={css`
          // Reverses the padding that one tap applies to the desktop mode
          #credential_picker_container {
            margin-left: -7px;
            margin-right: -7px;
          }

          // Allows the Google One Tap iframe to fit the size of the parent
          #credential_picker_container iframe {
            width: ${width} !important;
            margin: 0 auto;
            display: block;
          }

          // Fixes an issue where the default z-index value is hidden by the
          // sign up page
          #credential_picker_iframe {
            z-index: ${Zindexes.signUp} !important;
          }
        `}
      />
    </div>
  );
};

export default GoogleOneTapV2;
